/** Call from the click handler, before awaiting any asynchronously loaded text. */
export async function copyText(value: string | Promise<string>): Promise<void> {
  const clipboard = navigator.clipboard;
  if (
    typeof value !== "string" &&
    clipboard?.write &&
    typeof ClipboardItem !== "undefined"
  ) {
    const data = value.then((text) => new Blob([text], { type: "text/plain" }));
    // A rejected request must be handled even if the browser rejects the write first.
    void data.catch(() => {});
    try {
      // Safari requires write() during the click; the item's data can arrive later.
      await clipboard.write([new ClipboardItem({ "text/plain": data })]);
      return;
    } catch {
      // Older browsers may expose ClipboardItem without supporting promised data.
    }
  }

  const text = typeof value === "string" ? value : await value;
  if (clipboard?.writeText) {
    try {
      await clipboard.writeText(text);
      return;
    } catch {
      // Try the selection-based fallback when clipboard access is unavailable.
    }
  }

  const activeElement = document.activeElement;
  const selection = document.getSelection();
  const ranges = selection
    ? Array.from({ length: selection.rangeCount }, (_, i) =>
        selection.getRangeAt(i).cloneRange(),
      )
    : [];
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.readOnly = true;
  textarea.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0";
  // Elements outside an open modal dialog are inert and cannot be selected.
  (document.querySelector("dialog[open]") || document.body).append(textarea);
  try {
    textarea.focus();
    textarea.select();
    if (!document.execCommand("copy")) throw new Error("Clipboard unavailable");
  } finally {
    textarea.remove();
    if (activeElement instanceof HTMLElement) {
      activeElement.focus({ preventScroll: true });
    }
    if (selection) {
      selection.removeAllRanges();
      for (const range of ranges) selection.addRange(range);
    }
  }
}
